import React, { useState } from 'react';

import CardDialog from '~/components/CardDialog';
import ClaimLink from '~/components/ClaimLink';
import { SortableTable } from '~/components/core';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { CurrencyFormatterContextProvider } from '~/components/CurrencyFormatterContext';
import Filters from '~/components/Finances/FinanceApprovalsTab/PaymentApprovals/Filters';
import ReserveChangeRequestActionCell from '~/components/Finances/FinanceApprovalsTab/ReserveChangeRequestApprovals/ReserveChangeRequestActionCell';
import { AmountCurrencyFormattedCell } from '~/components/Finances/FinancesTable/AmountCurrencyFormattedCell/AmountCurrencyFormattedCell';
import useOrganization from '~/components/OrganizationContext';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import type { ReserveChangeRequestForApprovals } from '~/components/types/finances-types';
import useDataFetcher from '~/components/useDataFetcher';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import { formatSortOrder } from '~/Utils';

const RESULTS_PER_PAGE = 10;

interface ReserveChangeRequestsResponse {
  reserve_change_requests: ReserveChangeRequestForApprovals[];
  count: number;
}

const ReserveChangeRequestApprovals: React.FC = () => {
  const [page, setPage] = useState(0);
  const [sortByColumn, setSortByColumn] = useState({ id: 'request_date', order: 'desc' });
  const [filters, setFilters] = useState({});

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error TS233
  const { organizationId } = useOrganization();

  const params = {
    page_number: page + 1,
    results_per_page: RESULTS_PER_PAGE,
    sort_by_column: formatSortOrder(sortByColumn),
    request_status: 'pending',
    ...filters,
  };

  const { isLoading, isError, data, reloadData } = useDataFetcher<ReserveChangeRequestsResponse>(
    `/api/v1/organizations/${organizationId}/reserve_change_requests`,
    { params },
    !!organizationId
  );

  const columns = [
    {
      id: 'claim_id_display',
      label: 'Claim',
      specialCell: (row: ReserveChangeRequestForApprovals) => (
        <ClaimLink claimId={row.claim_id} linkText={row.claim_id_display} />
      ),
    },
    {
      id: 'requesting_user',
      label: 'Requesting user',
      disableSort: true,
      specialCell: (row: ReserveChangeRequestForApprovals) => row.requester_user?.username,
    },
    {
      id: 'decider_user',
      label: 'Approving User',
      disableSort: true,
      specialCell: (row: ReserveChangeRequestForApprovals) => row.decider_user?.username,
    },
    {
      id: 'request_date',
      label: 'Creation date',
      specialCell: (row: ReserveChangeRequestForApprovals) =>
        row.request_date && serverDateTimeToLocal(row.request_date),
    },
    {
      id: 'exposure_label',
      label: 'Exposure',
      disableSort: true,
      specialCell: (row: ReserveChangeRequestForApprovals) =>
        row.exposure_label ? row.exposure_label : 'General Expenses',
    },
    {
      id: 'request_amount',
      label: 'Amount',
      numeric: true,
      disableSort: true,
      specialCell: (row: ReserveChangeRequestForApprovals) => {
        return row.request_currency && row.request_amount_orig_currency ? (
          <CurrencyFormatterContextProvider currency={row.request_currency}>
            <AmountCurrencyFormattedCell amount={row.request_amount_orig_currency} />
          </CurrencyFormatterContextProvider>
        ) : (
          <AmountCurrencyFormattedCell amount={row.request_amount} />
        );
      },
    },
    {
      id: 'request_note',
      label: 'Note',
      disableSort: true,
      specialCell: (row: ReserveChangeRequestForApprovals) => (
        <OverflowTextWithToolTip maxWidth="300px">{row.request_note}</OverflowTextWithToolTip>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      disableSort: true,
      align: 'right',
      specialCell: (row: ReserveChangeRequestForApprovals) => (
        <ReserveChangeRequestActionCell row={row} onReloadFinances={() => reloadData()} />
      ),
    },
  ];

  const handleChange = (
    key: string,
    values: { payable_types_filter: string[]; requester_supervisor_user_id: string }
  ) => {
    setFilters((prevState) => ({ ...prevState, [key]: values }));
    setPage(0);
  };
  return (
    <CardDialog noCardTitle>
      <div className="p-20">
        <div>
          <Filters filters={filters} onChange={handleChange} />
        </div>
        <div>
          {isLoading || isError ? (
            <SkeletonTable columnsCount={columns.length} rowsCount={RESULTS_PER_PAGE} isError={isError} />
          ) : (
            <SortableTable
              rows={data?.reserve_change_requests ?? []}
              columns={columns}
              paginationProps={{
                page,
                rowsPerPage: RESULTS_PER_PAGE,
                onChangePage: (_: unknown, newPage: number) => {
                  setPage(newPage);
                },
                disabled: false,
                count: data?.count || 0,
                rowsPerPageOptions: [RESULTS_PER_PAGE],
              }}
              onSortByColumn={(_: unknown, sortByColumn: { id: string; order: string }) => {
                setSortByColumn(sortByColumn);
                setPage(0);
              }}
            />
          )}
        </div>
      </div>
    </CardDialog>
  );
};

export default ReserveChangeRequestApprovals;
